

import { IconPencil } from "@tabler/icons-react";

/**
 * UserIcon props interface
 * Defines all properties needed for the user avatar component
 */
type UserIconProps = {
  initialArgs?: string; // Optional override for the displayed initial
  monogramColorArgs?: string; // Optional override for the avatar background color (should be "1" through "20")
  onClickAction?: () => void; // Handler function for avatar click events
  penAction?: () => void; // Optional handler for edit icon click events
  size?: string; // Optional CSS classes to control avatar size
  fontSize?: string; // Optional CSS class for the initial text size
  read?: boolean; // Optional flag to indicate if the notification has been read
  margin?: string; // Optional CSS class for margin
  testId?: string; // Optional test id on the clickable avatar
};

// Color lookup object
const colorClasses = {
  "1": { bg: "user-icon-bg-1", font: "user-icon-font-1" },
  "2": { bg: "user-icon-bg-2", font: "user-icon-font-2" },
  "3": { bg: "user-icon-bg-3", font: "user-icon-font-3" },
  "4": { bg: "user-icon-bg-4", font: "user-icon-font-4" },
  "5": { bg: "user-icon-bg-5", font: "user-icon-font-5" },
  "6": { bg: "user-icon-bg-6", font: "user-icon-font-6" },
  "7": { bg: "user-icon-bg-7", font: "user-icon-font-7" },
  "8": { bg: "user-icon-bg-8", font: "user-icon-font-8" },
  "9": { bg: "user-icon-bg-9", font: "user-icon-font-9" },
  "10": { bg: "user-icon-bg-10", font: "user-icon-font-10" },
  "11": { bg: "user-icon-bg-11", font: "user-icon-font-11" },
  "12": { bg: "user-icon-bg-12", font: "user-icon-font-12" },
  "13": { bg: "user-icon-bg-13", font: "user-icon-font-13" },
  "14": { bg: "user-icon-bg-14", font: "user-icon-font-14" },
  "15": { bg: "user-icon-bg-15", font: "user-icon-font-15" },
  "16": { bg: "user-icon-bg-16", font: "user-icon-font-16" },
  "17": { bg: "user-icon-bg-17", font: "user-icon-font-17" },
  "18": { bg: "user-icon-bg-18", font: "user-icon-font-18" },
  "19": { bg: "user-icon-bg-19", font: "user-icon-font-19" },
  "20": { bg: "user-icon-bg-20", font: "user-icon-font-20" },
} as const;

/**
 * UserIcon component - Displays a circular avatar with user's initial
 * Creates a customizable user avatar that can include an optional edit button
 * @param props - Configuration properties for the UserIcon
 * @returns A circular user avatar with the user's initial and optional edit icon
 */
export const UserIcon: React.FC<UserIconProps> = ({
  initialArgs,
  monogramColorArgs,
  onClickAction,
  penAction,
  size = "w-6 h-6", // Default size if not specified
  fontSize = "body-3-medium", // Default font size if not specified
  read = true,
  margin,
  testId,
}) => {
  // Determine which initial to display - prioritize props over user data
  const initial = initialArgs ?? "";

  // Determine which color to use - prioritize props over user preferences, default to "1"
  const monogramColor = monogramColorArgs ?? "1";

  // Get the color classes from the lookup, fallback to "1" if invalid color
  const colors =
    colorClasses[monogramColor as keyof typeof colorClasses] ??
    colorClasses["1"];

  return (
    <div className={`relative inline-block w-max shrink-0 ${margin}`}>
      {/* User Icon */}
      <div
        onClick={onClickAction}
        data-testid={testId}
        className={`flex justify-center items-center ${size} ${
          colors.bg
        } rounded-full overflow-hidden ${
          onClickAction ? "cursor-pointer" : ""
        }`}
      >
        <div className={`${fontSize} ${colors.font}`}>{initial}</div>
      </div>

      {/* Green Dot Indicator (if read is false) */}
      {!read && (
        <span className="absolute top-0 left-0 w-3 h-3 surface-font-success border-2 outline-quinary rounded-full"></span>
      )}

      {/* Edit Icon */}
      {penAction && (
        <button
          className="absolute top-0 right-0 w-6 h-6 surface-primary border outline-quaternary rounded-full flex items-center justify-center"
          onClick={penAction}
        >
          <IconPencil size={14} className="font-primary" />
        </button>
      )}
    </div>
  );
};
